import { forwardRef, type ButtonHTMLAttributes, type MouseEventHandler } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@r/utils/utils.ts';
import { Loader2 } from 'lucide-react';

const defaultBaseStyles = [
  'inline-flex',
  'items-center',
  'justify-center',
  'gap-2',
  'whitespace-nowrap',
  'ring-offset-background',
  'transition-colors',
  'text-sm',
  'font-medium',
  '[&_svg]:pointer-events-none',
  '[&_svg]:size-4',
  '[&_svg]:shrink-0',
  'focus-visible:outline-none',
  'focus-visible:ring-2',
  'focus-visible:ring-ring',
  'focus-visible:ring-offset-2',
  'disabled:pointer-events-none',
  'disabled:opacity-50'
];

const customBaseStyles = [
  'inline-flex',
  'items-center',
  'justify-center',
  'gap-2',
  'whitespace-nowrap',
  'ring-offset-background',
  'transition-colors',
  'font-light',
  '[&_svg]:pointer-events-none',
  '[&_svg]:size-4',
  '[&_svg]:shrink-0',
  'disabled:pointer-events-none',
  'disabled:opacity-30'
];

const variantsStyles = {
  default: 'bg-primary text-primary-foreground hover:bg-primary/90',
  destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
  ghost: 'hover:bg-accent hover:text-accent-foreground',
  link: 'text-primary underline-offset-4 hover:underline',
  edit: 'text-foreground border-2 border-border bg-background hover:border-muted-foreground absolute right-[-1.125rem] bottom-0',
  raw: 'text-foreground hover:text-foreground/60'
};

export const buttonVariants = cva([], {
  variants: {
    base: {
      default: defaultBaseStyles,
      custom: customBaseStyles
    },
    variant: {
      default: variantsStyles.default,
      destructive: variantsStyles.destructive,
      outline: variantsStyles.outline,
      secondary: variantsStyles.secondary,
      ghost: variantsStyles.ghost,
      link: variantsStyles.link,
      edit: variantsStyles.edit,
      raw: variantsStyles.raw
    },
    rounded: {
      default: 'rounded-md',
      full: 'rounded-full'
    },
    size: {
      default: 'h-10 px-4 py-2',
      sm: 'h-9 px-3',
      lg: 'h-11 px-8',
      icon: 'h-10 w-10',
      custom: 'text-sm h-10 px-6',
      custom_sm: 'text-xs h-8 px-5',
      custom_lg: 'text-lg h-12 px-8',
      custom_icon: 'h-9 w-9'
    }
  },
  defaultVariants: {
    base: 'custom',
    variant: 'default',
    rounded: 'default',
    size: 'custom'
  }
});

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  base?: 'default' | 'custom';
  disabled?: boolean;
  backgroundColor?: string;
  color?: string;
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  asChild?: boolean;
  rounded?: 'default' | 'full';
  size?: 'default' | 'sm' | 'lg' | 'icon' | 'custom' | 'custom_sm' | 'custom_lg' | 'custom_icon';
  label?: string | null;
  icon?: string | null;
  iconRequiredPosition?: 'before' | 'after' | null;
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      base,
      variant,
      disabled,
      rounded,
      size,
      backgroundColor,
      color,
      onClick,
      asChild = false,
      loading = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        disabled={disabled || loading}
        onClick={onClick}
        className={cn(buttonVariants({ base, variant, rounded, size, className }))}
        style={{ backgroundColor, color }}
        ref={ref}
        {...props}
      >
        {loading && <Loader2 className="mr-2 w-5 h-5 animate-spin text-muted" />}
        <Slottable>{children}</Slottable>
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export { Button };
